import type { RegistryEntry, UiOption } from '../types';

// minimax-speech-2.8-turbo 与 minimax-speech-2.8-hd 共享同一套参数
const speechOptions: UiOption[] = [
  {
    key: 'voice_id',
    label: 'audio.voice_id',
    type: 'input',
    group: 'basic',
  },
  {
    key: 'speed',
    label: 'audio.speed',
    type: 'slider',
    min: 0.5,
    max: 2,
    step: 0.01,
    default: 1,
    group: 'basic',
  },
  {
    key: 'volume',
    label: 'audio.volume',
    type: 'slider',
    min: 0.1,
    max: 10,
    step: 0.01,
    default: 1,
    group: 'basic',
  },
  {
    key: 'pitch',
    label: 'audio.pitch',
    type: 'slider',
    min: -12,
    max: 12,
    step: 1,
    default: 0,
    group: 'basic',
  },
  {
    key: 'emotion',
    label: 'audio.emotion',
    type: 'select',
    options: [
      { label: 'Happy', value: 'happy' },
      { label: 'Sad', value: 'sad' },
      { label: 'Angry', value: 'angry' },
      { label: 'Fearful', value: 'fearful' },
      { label: 'Disgusted', value: 'disgusted' },
      { label: 'Surprised', value: 'surprised' },
      { label: 'Neutral', value: 'neutral' },
    ],
    default: 'neutral',
    group: 'advanced',
  },
  {
    key: 'format',
    label: 'audio.output_format',
    type: 'select',
    options: [
      { label: 'MP3', value: 'mp3' },
      { label: 'WAV', value: 'wav' },
      { label: 'PCM', value: 'pcm' },
      { label: 'FLAC', value: 'flac' },
    ],
    default: 'mp3',
    group: 'advanced',
  },
  {
    key: 'sample_rate',
    label: 'audio.sample_rate',
    type: 'select',
    options: [
      { label: '8000 Hz', value: '8000' },
      { label: '16000 Hz', value: '16000' },
      { label: '22050 Hz', value: '22050' },
      { label: '24000 Hz', value: '24000' },
      { label: '32000 Hz', value: '32000' },
      { label: '44100 Hz', value: '44100' },
    ],
    default: '32000',
    group: 'advanced',
  },
  {
    key: 'bitrate',
    label: 'audio.bitrate',
    type: 'select',
    options: [
      { label: '32 kbps', value: '32000' },
      { label: '64 kbps', value: '64000' },
      { label: '128 kbps', value: '128000' },
      { label: '256 kbps', value: '256000' },
    ],
    default: '128000',
    group: 'advanced',
  },
  {
    key: 'channel',
    label: 'audio.channel',
    type: 'select',
    options: [
      { label: 'Mono', value: '1' },
      { label: 'Stereo', value: '2' },
    ],
    default: '1',
    group: 'advanced',
  },
  {
    key: 'language_boost',
    label: 'audio.language_boost',
    type: 'select',
    options: [
      { label: 'Auto', value: 'auto' },
      { label: 'Chinese', value: 'Chinese' },
      { label: 'Chinese,Yue', value: 'Chinese,Yue' },
      { label: 'English', value: 'English' },
      { label: 'Japanese', value: 'Japanese' },
      { label: 'Korean', value: 'Korean' },
      { label: 'Spanish', value: 'Spanish' },
      { label: 'French', value: 'French' },
      { label: 'German', value: 'German' },
      { label: 'Portuguese', value: 'Portuguese' },
      { label: 'Italian', value: 'Italian' },
      { label: 'Russian', value: 'Russian' },
      { label: 'Arabic', value: 'Arabic' },
    ],
    default: 'auto',
    group: 'advanced',
  },
  {
    key: 'english_normalization',
    label: 'audio.english_normalization',
    type: 'switch',
    default: false,
    group: 'advanced',
  },
];

const speechToProviderOptions: RegistryEntry['toProviderOptions'] = (ui) => ({
  voice_id: typeof ui.voice_id === 'string' ? ui.voice_id : undefined,
  speed: typeof ui.speed === 'number' ? ui.speed : 1,
  volume: typeof ui.volume === 'number' ? ui.volume : 1,
  pitch: typeof ui.pitch === 'number' ? ui.pitch : 0,
  emotion: typeof ui.emotion === 'string' ? ui.emotion : 'neutral',
  format: typeof ui.format === 'string' ? ui.format : 'mp3',
  sample_rate:
    typeof ui.sample_rate === 'string'
      ? Number(ui.sample_rate)
      : typeof ui.sample_rate === 'number'
        ? ui.sample_rate
        : 32000,
  bitrate:
    typeof ui.bitrate === 'string'
      ? Number(ui.bitrate)
      : typeof ui.bitrate === 'number'
        ? ui.bitrate
        : 128000,
  channel: typeof ui.channel === 'string' ? ui.channel : '1',
  language_boost: typeof ui.language_boost === 'string' ? ui.language_boost : 'auto',
  english_normalization:
    typeof ui.english_normalization === 'boolean' ? ui.english_normalization : false,
});

export const minimaxConfigs: Record<string, RegistryEntry> = {
  'minimax-music-2.6': {
    options: [
      {
        key: 'is_instrumental',
        label: 'audio.instrumental',
        type: 'switch',
        default: false,
        group: 'basic',
      },
      {
        key: 'bitrate',
        label: 'audio.bitrate',
        type: 'select',
        options: [
          { label: '32 kbps', value: '32000' },
          { label: '60 kbps', value: '60000' },
          { label: '64 kbps', value: '64000' },
          { label: '128 kbps', value: '128000' },
          { label: '256 kbps', value: '256000' },
        ],
        default: '256000',
        group: 'advanced',
      },
      {
        key: 'sample_rate',
        label: 'audio.sample_rate',
        type: 'select',
        options: [
          { label: '16000 Hz', value: '16000' },
          { label: '24000 Hz', value: '24000' },
          { label: '32000 Hz', value: '32000' },
          { label: '44100 Hz', value: '44100' },
        ],
        default: '44100',
        group: 'advanced',
      },
    ],
    toProviderOptions: (ui) => ({
      is_instrumental: typeof ui.is_instrumental === 'boolean' ? ui.is_instrumental : false,
      bitrate: ui.bitrate ? Number(ui.bitrate) : 256000,
      sample_rate: ui.sample_rate ? Number(ui.sample_rate) : 44100,
    }),
  },
  'minimax-music-3.0': {
    options: [
      {
        key: 'is_instrumental',
        label: 'audio.instrumental',
        type: 'switch',
        default: false,
        group: 'basic',
      },
      {
        key: 'lyrics_optimizer',
        label: 'audio.lyrics_optimizer',
        type: 'switch',
        default: false,
        group: 'basic',
      },
      {
        key: 'bitrate',
        label: 'audio.bitrate',
        type: 'select',
        options: [
          { label: '32 kbps', value: '32000' },
          { label: '60 kbps', value: '60000' },
          { label: '64 kbps', value: '64000' },
          { label: '128 kbps', value: '128000' },
          { label: '256 kbps', value: '256000' },
        ],
        default: '256000',
        group: 'basic',
      },
      {
        key: 'sample_rate',
        label: 'audio.sample_rate',
        type: 'select',
        options: [
          { label: '16000 Hz', value: '16000' },
          { label: '24000 Hz', value: '24000' },
          { label: '32000 Hz', value: '32000' },
          { label: '44100 Hz', value: '44100' },
        ],
        default: '44100',
        group: 'basic',
      },
    ],
    toProviderOptions: (ui) => ({
      is_instrumental: typeof ui.is_instrumental === 'boolean' ? ui.is_instrumental : false,
      lyrics_optimizer: typeof ui.lyrics_optimizer === 'boolean' ? ui.lyrics_optimizer : false,
      bitrate: ui.bitrate ? Number(ui.bitrate) : 256000,
      sample_rate: ui.sample_rate ? Number(ui.sample_rate) : 44100,
    }),
  },
  'minimax-voice-clone': {
    options: [
      {
        key: 'need_noise_reduction',
        label: 'audio.noise_reduction',
        type: 'switch',
        default: false,
        group: 'basic',
      },
      {
        key: 'need_volume_normalization',
        label: 'audio.volume_normalization',
        type: 'switch',
        default: false,
        group: 'basic',
      },
      {
        key: 'accuracy',
        label: 'audio.accuracy',
        type: 'slider',
        min: 0,
        max: 1,
        step: 0.01,
        default: 0.7,
        group: 'basic',
      },
    ],
    toProviderOptions: (ui) => ({
      custom_voice_id: typeof ui.custom_voice_id === 'string' ? ui.custom_voice_id : '',
      need_noise_reduction:
        typeof ui.need_noise_reduction === 'boolean'
          ? ui.need_noise_reduction
          : false,
      need_volume_normalization:
        typeof ui.need_volume_normalization === 'boolean'
          ? ui.need_volume_normalization
          : false,
      accuracy: typeof ui.accuracy === 'number' ? ui.accuracy : 0.7,
    }),
  },
  'minimax-speech-2.8-turbo': {
    options: speechOptions,
    toProviderOptions: speechToProviderOptions,
  },
  'minimax-speech-2.8-hd': {
    options: speechOptions,
    toProviderOptions: speechToProviderOptions,
  },
};